<script setup lang="ts">
import {
  type LanguageType,
  NuvoImporter,
  type OnResults,
  type SettingsAPI,
} from '@getnuvo/importer-vue';
import useUploadQuestionsJsonMutation from '@/api/mutations/CustomQuestionnaire/uploadQuestionsJson.mutation';

type Props = {
  customQuestionnaireId: string;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  uploaded: [];
}>();

const { mutate: addQuestionsJson } = useUploadQuestionsJsonMutation();

const settings: SettingsAPI = {
  developerMode: window.location.hostname !== 'app.codioimpact.com',
  identifier: 'questions',
  modal: false,
  language: (localStorage.getItem('codioImpactLang') as LanguageType) ?? 'en',
  columns: [
    {
      key: 'name',
      label: 'Name of DataPoint',
      description:
        "This should be a descriptive name of the datapoint. Given the question is 'specify the number of cars in your company', the name could be 'Company cars'",
      example: 'Screening for IROs water and marine resources',
      columnType: 'string',
    },
    {
      key: 'question',
      label: 'Question',
      description: 'This is the actual question that the user will answer',
      example: 'How many company cars do you have?',
      columnType: 'string',
      // validations: [
      //   {
      //     validate: 'required',
      //     errorMessage: 'A question is required',
      //   },
      // ],
    },
    {
      key: 'type',
      label: 'Question type',
      description: 'Type of question',
      columnType: 'category',
      dropdownOptions: [
        {
          label: 'Number',
          value: 'numeric',
          type: 'string',
        },
        {
          label: 'Long text (more than a sentence)',
          value: 'text_long',
          type: 'string',
        },
        {
          label: 'Short text (words until max 1 sentence)',
          value: 'text',
          type: 'string',
        },
        {
          label: 'Multiple numbers',
          value: 'numeric_split',
          type: 'string',
        },
        {
          label: 'Multiple Choice',
          value: 'multiple_choice',
          type: 'string',
        },
        {
          label: 'Choice',
          value: 'choice',
          type: 'string',
        },
        {
          label: 'Yes / No',
          value: 'boolean',
          type: 'string',
        },
      ],
      isMultiSelect: false,
    },
    {
      key: 'choices',
      label: 'Choices',
      description:
        "Only required if 'choice' or 'multiple_choice' is used as question type. Choose this if you have a single field containing all possible choices. Each separate choice must be separated using '###SEPARATOR###'. For example: 'Yes###SEPARATOR###No###SEPARATOR###Maybe'",
      columnType: 'string',
    },
    {
      key: 'choice1',
      label: 'Choice 1',
      description:
        "Only required if 'choice' or 'multiple_choice' is used as question type. Do not use this in combination with 'choices', but 'choice1', 'choice2', ...",
      columnType: 'string',
    },
    {
      key: 'choice2',
      label: 'Choice 2',
      description:
        "Only required if 'choice' or 'multiple_choice' is used as question type. Do not use this in combination with 'choices', but 'choice1', 'choice2', ...",
      columnType: 'string',
    },
    {
      key: 'choice3',
      label: 'Choice 3',
      description:
        "Only required if 'choice' or 'multiple_choice' is used as question type. Do not use this in combination with 'choices', but 'choice1', 'choice2', ...",
      columnType: 'string',
    },
    {
      key: 'choice4',
      label: 'Choice 4',
      description:
        "Only required if 'choice' or 'multiple_choice' is used as question type. Do not use this in combination with 'choices', but 'choice1', 'choice2', ...",
      columnType: 'string',
    },
    {
      key: 'choice5',
      label: 'Choice 5',
      description:
        "Only required if 'choice' or 'multiple_choice' is used as question type. Do not use this in combination with 'choices', but 'choice1', 'choice2', ...",
      columnType: 'string',
    },
    {
      key: 'choice6',
      label: 'Choice 6',
      description:
        "Only required if 'choice' or 'multiple_choice' is used as question type. Do not use this in combination with 'choices', but 'choice1', 'choice2', ...",
      columnType: 'string',
    },
    {
      key: 'choice7',
      label: 'Choice 7',
      description:
        "Only required if 'choice' or 'multiple_choice' is used as question type. Do not use this in combination with 'choices', but 'choice1', 'choice2', ...",
      columnType: 'string',
    },
    {
      key: 'choice8',
      label: 'Choice 8',
      description:
        "Only required if 'choice' or 'multiple_choice' is used as question type. Do not use this in combination with 'choices', but 'choice1', 'choice2', ...",
      columnType: 'string',
    },
    {
      key: 'choice9',
      label: 'Choice 9',
      description:
        "Only required if 'choice' or 'multiple_choice' is used as question type. Do not use this in combination with 'choices', but 'choice1', 'choice2', ...",
      columnType: 'string',
    },
    {
      key: 'choice10',
      label: 'Choice 10',
      description:
        "Only required if 'choice' or 'multiple_choice' is used as question type. Do not use this in combination with 'choices', but 'choice1', 'choice2', ...",
      columnType: 'string',
    },
    {
      key: 'questionHelp',
      label: 'Help text',
      description: 'An optional help text describing the question',
      columnType: 'string',
    },
    {
      key: 'tags',
      label: 'Tags',
      description:
        "Optional comma-separated list of tags (e.g., 'Environment, Scope 1, Energy'). Tags are auto-created if they don't exist.",
      columnType: 'string',
    },
  ],
};

const nuvoLicenseKey = import.meta.env.VITE_NUVO_LICENSE;

const onResults: OnResults = async (results, errors, complete) => {
  const newRows = [];

  let lastQuestionIndex = -1;
  for (const row of results) {
    if (row.question) {
      const numberOfPossibleChoices = 10;

      for (let i = 1; i <= numberOfPossibleChoices; i++) {
        if (row[`choice${i}`]) {
          row.choices ??= '';
          if (i > 1) {
            row.choices += '###SEPARATOR###';
          }
          row.choices += `${row[`choice${i}`]}`;
        } else {
          break;
        }
      }

      newRows.push(row);
      lastQuestionIndex = newRows.length - 1;
    }

    if (!row.question && row.choices && lastQuestionIndex >= 0) {
      newRows[lastQuestionIndex].type = 'choice';

      if (!newRows[lastQuestionIndex].choices) {
        newRows[lastQuestionIndex].choices = '';
        newRows[lastQuestionIndex].questionHelp = '';
      } else {
        newRows[lastQuestionIndex].choices += '###SEPARATOR###';
        newRows[lastQuestionIndex].questionHelp += '\r\n\r\n';
      }

      newRows[lastQuestionIndex].choices += `${row.choices}`;
      newRows[lastQuestionIndex].questionHelp += `${row.questionHelp}`;
    }
  }

  const data = [];

  for (const row of newRows) {
    const choices = (row.choices as string)?.split('###SEPARATOR###') ?? [];

    const tags =
      typeof row.tags === 'string'
        ? row.tags
            .split(',')
            .map((t: string) => t.trim())
            .filter(Boolean)
        : [];

    data.push({
      ...row,
      type: row.type ?? 'text_long',
      choices,
      description: row.questionHelp,
      tags,
    });
  }

  await addQuestionsJson(
    {
      customQuestionnaireId: props.customQuestionnaireId,
      questions: JSON.stringify(data),
    },
    {
      update: (store) => {
        store.evict({
          fieldName: 'getCustomQuestionnaire',
        });
      },
    },
  );

  emit('uploaded');

  if (complete) {
    complete();
  }
};
</script>

<template>
  <NuvoImporter
    :settings="settings"
    :licenseKey="nuvoLicenseKey"
    :onResults="onResults"
  />
</template>
